import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import braintree from 'braintree-web';
import { useTranslation } from 'react-i18next';

const textAlign = navigator.language.startsWith('ar') ? 'right' : 'left';

const fontColor = {
  Geozilla: '#000000',
  'Family Locator': '#3393EC',
  Familo: '#323232',
};

const defaultFieldsStyles = (productName = 'Geozilla') => {
  return {
    base: {
      color: fontColor[productName],
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: 300,
      fontFamily: 'Open Sans, Segoe UI, sans-serif',
      textAlign,

      '::placeholder': {
        color: '#ADADAD',
        letterSpacing: '1.2px',
        fontSize: '18px',
        lineHeight: '24px',
      },
    },
  };
};

const braintreeDefaultFieldsStyles = (productName = 'Geozilla') => {
  return {
    input: {
      'font-size': '18px',
      color: fontColor[productName],
      'line-height': '24px',
      'font-weight': '400',
      'font-family': 'Open Sans, Segoe UI, sans-serif',
      'text-align': textAlign,
      height: '28px',
    },
    'input::placeholder': {
      'font-size': '18px',
      color: '#ADADAD',
      'line-height': '24px',
      'letter-spacing': '1.2px',
    },
    '.invalid': {
      color: '#eb1c26',
    },
  };
};

const usePaymentInit = ({
  productName,
  settingsStatus,
  paymentProvider,
  stripePk,
  btToken,
  withPaypal,
  stripeFieldsStyle = defaultFieldsStyles(productName),
  braintreeFieldsStyle = braintreeDefaultFieldsStyles(productName),
}) => {
  const { t } = useTranslation();
  const cardPlaceholder = '0000 0000 0000 0000';
  const expirationPlaceholder = t('MM/YY');
  const cvcPlaceholder = t('CVV');
  const [stripePromise, setStripePromise] = useState(null);
  const [braintreeInstance, setBraintreeInstance] = useState(null);
  const [paypalInstance, setPaypalInstance] = useState(null);
  const stripeInstance = useRef();
  const card = useRef();
  const expiration = useRef();
  const cvc = useRef();
  const cardholder = useRef(null);
  const braintreeCardInstance = useRef();
  const braintreeThreeDSInstance = useRef();

  const initBraintree = useCallback(() => {
    if (!btToken) return;

    braintree.client.create({ authorization: btToken }, (clientErr, clientInstance) => {
      if (clientErr) {
        console.error('Error creating client:', clientErr);
        return;
      }

      if (paymentProvider === 'braintree') {
        setBraintreeInstance(clientInstance);
      }

      if (withPaypal) {
        braintree.paypal.create({ client: clientInstance }, (paypalErr, paypalInstance) => {
          if (paypalErr) {
            console.error('Error creating PayPal:', paypalErr);
            return;
          }

          setPaypalInstance(paypalInstance);
        });
      }
    });

    if (paymentProvider === 'braintree') {
      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      document.body.appendChild(script);
    }
  }, [btToken, withPaypal, paymentProvider]);

  const initStripe = useCallback(() => {
    if (!stripePk) return;

    setStripePromise(loadStripe(stripePk));

    if (withPaypal) {
      initBraintree();
    }
  }, [initBraintree, withPaypal, stripePk]);

  const providerPackage = useMemo(() => {
    return {
      braintree: {
        init: initBraintree,
      },
      stripe: {
        init: initStripe,
      },
    };
  }, [initStripe, initBraintree]);

  useEffect(() => {
    if (settingsStatus && paymentProvider) {
      providerPackage[paymentProvider].init();
    }
  }, [settingsStatus, paymentProvider, providerPackage]);

  useEffect(() => {
    if (stripePromise) {
      stripePromise.then((stripeObj) => {
        const elements = stripeObj.elements();

        card.current = elements.create('cardNumber', {
          style: stripeFieldsStyle,
          placeholder: cardPlaceholder,
        });
        expiration.current = elements.create('cardExpiry', {
          style: stripeFieldsStyle,
          placeholder: expirationPlaceholder,
        });
        cvc.current = elements.create('cardCvc', {
          style: stripeFieldsStyle,
          placeholder: cvcPlaceholder,
        });

        stripeInstance.current = stripeObj;
      });
    }
  }, [stripePromise, stripeFieldsStyle, expirationPlaceholder, cvcPlaceholder]);

  return {
    paymentInstances: {
      stripePromise,
      braintreeInstance,
      paypalInstance,
    },
    paymentRefs: {
      stripeInstance,
      card,
      expiration,
      cvc,
      cardholder,
      braintreeCardInstance,
      braintreeThreeDSInstance,
    },
  };
};

export default usePaymentInit;
